import { useState } from 'react';
import { WEB_ICO } from './icons';
import type { ExerciseSection } from '../api/content.types';
import type { ExerciseAttemptSummary } from '../api/progress.types';
import { HIGH_SCORE_PCT, formatAttemptMeta, scorePct } from './exerciseHistoryFormat';

const SECTION_VI: Record<ExerciseSection, string> = { VOCAB: 'Từ vựng', GRAMMAR: 'Ngữ pháp', READING: 'Đọc hiểu' };

function SectionBar({ label, correct, total }: { label: string; correct: number; total: number }) {
  const full = total > 0 && correct === total;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
      <span style={{ width: 70 }}>{label}</span>
      <span style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--surface-tint)', overflow: 'hidden' }}>
        <i style={{ display: 'block', height: '100%', width: `${scorePct(correct, total)}%`, background: full ? 'var(--success)' : 'var(--cta-default)' }} />
      </span>
      <span style={{ width: 30, textAlign: 'right', color: 'var(--ink)' }}>{`${correct}/${total}`}</span>
    </div>
  );
}

function AttemptCard({ attempt, open, onToggle, onRetry }: { attempt: ExerciseAttemptSummary; open: boolean; onToggle: () => void; onRetry: () => void }) {
  const pct = scorePct(attempt.correct, attempt.total);
  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-card)', background: 'var(--surface-page)' }}>
      <button
        onClick={onToggle}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          width: '100%',
          padding: '12px 14px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'var(--font-vi)',
        }}
      >
        <span
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            flexShrink: 0,
            border: `2.5px solid ${pct >= HIGH_SCORE_PCT ? 'var(--success)' : 'var(--cta-default)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13.5,
            fontWeight: 700,
            color: 'var(--ink)',
          }}
        >
          {pct}%
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14.5, fontWeight: 700, color: 'var(--ink)' }}>{`${attempt.correct}/${attempt.total} câu đúng`}</span>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{formatAttemptMeta(attempt)}</span>
        </span>
        <span style={{ display: 'flex', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 150ms ease' }}>{WEB_ICO.chev('var(--navy-300)', 16)}</span>
      </button>

      {open && (
        <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {attempt.sectionScores.map((s) => (
            <SectionBar key={s.section} label={SECTION_VI[s.section]} correct={s.correct} total={s.total} />
          ))}
          <button
            onClick={onRetry}
            style={{
              marginTop: 4,
              height: 40,
              borderRadius: 'var(--radius-control)',
              background: 'transparent',
              border: '1px solid var(--cta-default)',
              color: 'var(--cta-default)',
              cursor: 'pointer',
              fontFamily: 'var(--font-vi)',
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            Làm lại đề này
          </button>
        </div>
      )}
    </div>
  );
}

export interface WebExerciseHistoryListProps {
  attempts: ExerciseAttemptSummary[];
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  onRetry: (attempt: ExerciseAttemptSummary) => void;
}

/** Tab "Lịch sử" trong WebExerciseDialog — khớp ExerciseHistoryList (mobile). Lượt mới nhất mở sẵn. */
export function WebExerciseHistoryList({ attempts, hasMore, loadingMore, onLoadMore, onRetry }: WebExerciseHistoryListProps) {
  // undefined = chưa bấm lần nào → theo mặc định (chỉ lượt đầu mở).
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (attempts.length === 0) {
    return (
      <div style={{ padding: '48px 16px', textAlign: 'center', fontFamily: 'var(--font-vi)' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>Chưa có lượt làm nào</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>Làm xong đề, kết quả sẽ được lưu ở đây.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {attempts.map((a, i) => {
        const open = expanded[a.id] ?? i === 0;
        return <AttemptCard key={a.id} attempt={a} open={open} onToggle={() => setExpanded((e) => ({ ...e, [a.id]: !open }))} onRetry={() => onRetry(a)} />;
      })}
      {hasMore && (
        <button
          onClick={onLoadMore}
          disabled={loadingMore}
          style={{
            alignSelf: 'center',
            height: 40,
            padding: '0 18px',
            background: 'none',
            border: 'none',
            color: 'var(--cta-default)',
            cursor: loadingMore ? 'default' : 'pointer',
            fontFamily: 'var(--font-vi)',
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          {loadingMore ? 'Đang tải…' : 'Xem thêm'}
        </button>
      )}
    </div>
  );
}
